/**
 * Key markers — invisible i18next-key tags for the translation key inspector.
 *
 * While inspect mode is on (see `useKeyInspector`), every `t()` result gets an
 * invisible suffix that encodes which key produced it. Clicking any text in
 * the app can then recover the exact key, instead of guessing from the
 * rendered string.
 *
 * Originally this used the `i18next-subliminal` package, which does the same
 * thing but spells the key, namespace, language and source out as JSON in
 * binary, using two invisible characters per bit — about 750 invisible
 * characters for a 13-character string. That's fine for occasional use, but
 * inspect mode marks *every* string on the page, including ones that flow
 * into toasts, CSV exports, and audit notes if a guard is missed. This module
 * instead keeps a runtime `key → small integer id` registry for the current
 * tab and encodes only the id, in base 4. A marker is typically 4-6 invisible
 * characters, not ~750, which makes any string-length-sensitive code far less
 * likely to notice inspect mode is on even before the request/export/print
 * guards kick in.
 *
 * Markers are session-local: the id → key table lives in memory only, is
 * never persisted, and is rebuilt from scratch on reload. A marker copied out
 * of the tab that created it (into a screenshot, a saved file, another tab)
 * decodes to nothing — {@link decodeKeyMarkers} simply returns no matches for
 * ids the current tab never assigned. This is deliberate: markers should
 * never accidentally leak meaning outside the session that created them.
 *
 * The i18next postProcessor that applies these markers ({@link keyMarkerPostProcessor})
 * and its on/off switch ({@link setKeyMarkersEnabled}) live in this file too,
 * rather than in `core/plugins/i18n.ts` where they're registered. That file
 * uses `import.meta.env` at the top of `initI18n()`, which TypeScript can only
 * compile against an ES module target — importing it at all fails to parse
 * under this app's Jest config (`ts-jest` compiles to CommonJS), which is why
 * `jest.config.js` maps `@/core/plugins/i18n` to a stub for every other spec.
 * Keeping the marker logic in this Vite-free leaf module is what makes it
 * testable at all.
 */
import i18next from 'i18next';

/**
 * i18next's default namespace name, duplicated from `core/plugins/i18n.ts`'s
 * `DEFAULT_NAMESPACE` rather than imported from it — importing anything from
 * that file, even one constant, forces Jest to parse the whole module and
 * hit the `import.meta` error described in the module docblock above. This
 * is i18next's own built-in default ('translation' unless configured
 * otherwise), and `initI18n()` never changes it, so the duplication is safe.
 */
const DEFAULT_NAMESPACE_FALLBACK = 'translation';

/**
 * Digit alphabet, base 4. All four characters are zero-width and render
 * identically (invisibly) in every browser and in plain-text contexts
 * (terminals, `<textarea>`, clipboard) — unlike wider Unicode marks, none of
 * these are stripped by HTML sanitizers that target zero-width space
 * specifically, because none of the four *is* U+200B alone; each digit still
 * needs U+200B in the alphabet, so a sanitizer that strips only U+200B would
 * corrupt digit "0" — see the note on {@link stripKeyMarkers} for why this is
 * a strip-only-while-inspecting tool, not a permanent artifact.
 */
const DIGIT_CHARS = ['​', '‌', '‍', '⁠'] as const;
const START_CHAR = '⁣'; // INVISIBLE SEPARATOR
const END_CHAR = '⁤'; // INVISIBLE PLUS

const DIGIT_TO_VALUE = new Map(DIGIT_CHARS.map((ch, i) => [ch, i]));

/**
 * Matches one full marker: START, one or more digits, END. Global + unicode
 * flags so a string with several markers (e.g. code that concatenates two
 * `t()` results) yields every one, in the order they appear.
 *
 * Built as an alternation, not a `[...]` character class: several of the
 * digit characters (e.g. U+200D ZERO WIDTH JOINER) trip up eslint's
 * `no-misleading-character-class`, which flags codepoints that combine with
 * their neighbors when grouped in a class — a real concern for that rule in
 * general, but not for these, since they're matched as standalone literals
 * here, never combined with each other.
 */
const DIGIT_ALTERNATION = DIGIT_CHARS.join('|');
const MARKER_RE = new RegExp(`${START_CHAR}(?:${DIGIT_ALTERNATION})+${END_CHAR}`, 'gu');

// ── Registry ─────────────────────────────────────────────────────────────────

/** key (namespace-qualified, e.g. "translation:patient.addNew") → id. */
const keyToId = new Map<string, number>();
/** id → key, the reverse of {@link keyToId}. */
const idToKey: string[] = [];

function qualify(key: string, ns: string | undefined): string {
  return `${ns ?? ''}:${key}`;
}

/**
 * Look up (or assign) the id for a key, and return its encoded marker.
 *
 * Ids are assigned in first-seen order for the lifetime of the tab. Nothing
 * here is persisted — a fresh reload gets a fresh, empty registry, and any
 * marker minted before the reload decodes to nothing afterwards.
 */
export function encodeKeyMarker(key: string, ns?: string): string {
  const qualified = qualify(key, ns);
  let id = keyToId.get(qualified);
  if (id === undefined) {
    id = idToKey.length;
    keyToId.set(qualified, id);
    idToKey.push(qualified);
  }
  return START_CHAR + encodeId(id) + END_CHAR;
}

/** Base-4 encode, using the shortest representation (no fixed width, no padding — {@link MARKER_RE}'s END_CHAR marks where the digits stop, so length is free to grow with the registry). */
function encodeId(id: number): string {
  if (id === 0) return DIGIT_CHARS[0];
  let n = id;
  let out = '';
  while (n > 0) {
    out = DIGIT_CHARS[n % 4] + out;
    n = Math.floor(n / 4);
  }
  return out;
}

function decodeId(digits: string): number | undefined {
  let n = 0;
  for (const ch of digits) {
    const v = DIGIT_TO_VALUE.get(ch);
    if (v === undefined) return undefined;
    n = n * 4 + v;
  }
  return n;
}

// ── Public API ───────────────────────────────────────────────────────────────

export type DecodedKeyMarker = {
  /** The translation key, without namespace. */
  key: string;
  /** The i18next namespace, or `undefined` if none was recorded. */
  ns?: string;
};

/**
 * True if `text` contains at least one marker. Cheap enough to call on every
 * hovered element without the full decode in {@link decodeKeyMarkers}.
 *
 * Resets `MARKER_RE.lastIndex` first: `MARKER_RE` is a single shared, global
 * (`/g`) regex, and `.test()` on a global regex resumes from wherever
 * `lastIndex` was left by the *previous* call — on any string, not just this
 * one. Skipping the reset here previously left `lastIndex` pointing past the
 * end of a short marked string, which then made the *next* call — often
 * {@link decodeKeyMarkers} or {@link stripKeyMarkers} on a different, longer
 * string — silently start searching from that leftover offset and find
 * nothing. Each of those three functions resets it independently, so none of
 * them depends on which order the others were called in.
 */
export function containsKeyMarker(text: string): boolean {
  if (!text.includes(START_CHAR)) return false;
  MARKER_RE.lastIndex = 0;
  return MARKER_RE.test(text);
}

/**
 * Decode every marker in `text`, left to right. Most rendered text carries at
 * most one — `{{ t('key') }}` in a template produces exactly one marker per
 * text node. More than one shows up only when app code concatenates several
 * `t()` results into one string; in that case the first entry is the best
 * guess at the "primary" key for that text, with the rest listed alongside it
 * in the inspector popover.
 *
 * An id with no matching entry in the registry (copied from another tab, or
 * minted before a reload) is silently skipped rather than throwing — see the
 * module docblock on why markers are deliberately session-local.
 */
export function decodeKeyMarkers(text: string): DecodedKeyMarker[] {
  // See the comment on containsKeyMarker's reset — matchAll on a global
  // regex resumes from the shared object's current lastIndex, not from 0.
  MARKER_RE.lastIndex = 0;
  const results: DecodedKeyMarker[] = [];
  for (const match of text.matchAll(MARKER_RE)) {
    const digits = match[0].slice(START_CHAR.length, -END_CHAR.length);
    const id = decodeId(digits);
    if (id === undefined) continue;
    const qualified = idToKey[id];
    if (qualified === undefined) continue;
    const sep = qualified.indexOf(':');
    const ns = sep > 0 ? qualified.slice(0, sep) : undefined;
    const key = sep >= 0 ? qualified.slice(sep + 1) : qualified;
    results.push({ key, ns });
  }
  return results;
}

/**
 * Remove every marker from `text`, leaving the visible text untouched.
 * Idempotent, and a no-op on text with no markers — safe to call
 * unconditionally on anything that might have passed through `t()` while
 * inspect mode was on: `resolveBundleDefault()` in `useMrnLabel`/
 * `usePatientLabel`, outgoing request bodies, and clipboard contents.
 */
export function stripKeyMarkers(text: string): string {
  // See the comment on containsKeyMarker's reset — replace on a global
  // regex resumes from the shared object's current lastIndex, not from 0.
  MARKER_RE.lastIndex = 0;
  return text.replace(MARKER_RE, '');
}

/**
 * Recursively strip markers from every string reachable inside `value`,
 * returning a value of the same shape. Arrays and plain objects are walked;
 * everything else (numbers, booleans, `null`, `Date`, `File`, `FormData`,
 * ...) is returned as-is.
 *
 * Used by the axios request interceptor in `core/services/api.service.ts` to
 * sanitize outgoing request bodies while inspect mode is on — a field whose
 * value defaulted from a marked `t()` call (e.g. a prefilled note or label)
 * would otherwise ship the invisible marker to the backend.
 *
 * Always rebuilds arrays/objects rather than mutating in place, so a caller
 * passing a reactive Vue object or a shared constant is never written to.
 */
export function deepStripKeyMarkers<T>(value: T): T {
  if (typeof value === 'string') {
    return (containsKeyMarker(value) ? stripKeyMarkers(value) : value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepStripKeyMarkers(item)) as unknown as T;
  }
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepStripKeyMarkers(v);
    }
    return out as T;
  }
  return value;
}

/**
 * Clear the registry. Call on logout — a stale registry from a previous
 * session is harmless (ids just get reused for possibly-different keys), but
 * clearing keeps id 0 meaning the same thing as "first key seen this
 * session" rather than accumulating across logins in a long-lived tab.
 */
export function resetKeyMarkerRegistry(): void {
  keyToId.clear();
  idToKey.length = 0;
}

// ── i18next postProcessor and toggle ─────────────────────────────────────────

/**
 * Module-level "is inspect mode on" flag. Deliberately a plain boolean, not
 * a Vue ref — this file has no Vue dependency, and `process()` below runs on
 * every `t()` call, so a plain read is cheaper than going through reactivity
 * for something checked on every render. `useKeyInspector` is the reactive
 * source of truth for the UI; it calls {@link setKeyMarkersEnabled} to move
 * this flag.
 */
let keyMarkersEnabled = false;

/**
 * i18next postProcessor module. Registered once, unconditionally, in both
 * `initI18n()` init() calls in `core/plugins/i18n.ts` — turning inspect mode
 * on/off only flips {@link keyMarkersEnabled}, never `i18next.options.postProcess`
 * or the module registration itself, so there's nothing to re-init.
 *
 * Requires `postProcessPassResolved: true` in the init options (set in
 * `initI18n()`) for `options.i18nResolved` to be populated — without it,
 * `process()` still works (it falls back to the raw `keys` array), but
 * `i18nResolved.exactUsedKey` is what correctly identifies which key resolved
 * when a call passes several fallback keys.
 */
export const keyMarkerPostProcessor = {
  type: 'postProcessor' as const,
  name: 'keyMarker',
  process(value: string, keys: string | string[], options: Record<string, unknown>): string {
    if (!keyMarkersEnabled) return value;
    if (typeof value !== 'string' || !value) return value;

    const resolved = options?.i18nResolved as
      | { exactUsedKey?: string; usedNS?: string }
      | undefined;
    const key = resolved?.exactUsedKey ?? (Array.isArray(keys) ? keys[0] : keys);
    const ns = resolved?.usedNS ?? DEFAULT_NAMESPACE_FALLBACK;
    const ownMarker = encodeKeyMarker(key, ns);

    // Guards against double-marking this exact call's own result (e.g. if
    // this postProcessor somehow ran twice for the same key) — checked
    // against *this* key's own marker specifically, not "contains any
    // marker at all". A blanket containsKeyMarker() check here would also
    // suppress a legitimate marker for a composed translation —
    // t(key, { someVar: t(innerKey) }) — whose interpolated value is itself
    // an already-marked string from an unrelated t() call. That case must
    // still get *this* call's own marker appended, or the key responsible
    // for text like a button's own aria-label (built from an outer t() call
    // interpolating an inner one) never gets marked at all.
    if (value.endsWith(ownMarker)) return value;

    return value + ownMarker;
  },
};

/**
 * Turn key-inspector markers on or off for every subsequent `t()` call.
 *
 * `i18next.store.emit('added')` re-renders every mounted `$t()` binding with
 * (or without) markers — the same mechanism `setMrnLabel`/`setPatientLabel`
 * use to refresh bindings after publishing a new tenant term. Safe to call
 * before i18next has initialised — `store` is `undefined` until then, and
 * `?.` short-circuits.
 */
export function setKeyMarkersEnabled(on: boolean): void {
  keyMarkersEnabled = on;
  i18next.store?.emit?.('added');
}

/** Whether key-inspector markers are currently appended to `t()` output. */
export function areKeyMarkersEnabled(): boolean {
  return keyMarkersEnabled;
}
